import { Directive, Input, ElementRef, HostListener, Renderer2, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';

@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective {
  @Input('appTooltip') tooltipTitle = '';
  @Input() placement = 'top';
  @Input() delay = 100;
  tooltip?: HTMLElement;
  offset = 10;

  constructor(private el: ElementRef, private renderer: Renderer2, private router: Router, private cdRef: ChangeDetectorRef) {}

  ngOnInit() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.tooltip) {
          this.hide();
        }
      }
    });
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (!this.tooltip) {
      this.show();
      this.cdRef.detectChanges();
    }
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (this.tooltip) {
      this.hide();
      this.cdRef.detectChanges();
    }
  }

  @HostListener('click') onMouseClick() {
    if (this.tooltip) {
      this.hide();
      this.cdRef.detectChanges();
    }
  }

  show() {
    this.removeExistingTooltips();
    this.create();
    // Wait for tooltip to render before calculating position
    requestAnimationFrame(() => {
      this.setPosition();
      this.renderer.addClass(this.tooltip, 'ng-tooltip-show');
    });
  }
  
  private removeExistingTooltips() {
    const existingTooltips = document.querySelectorAll('.ng-tooltip');
    existingTooltips.forEach(tooltip => {
      this.renderer.removeChild(document.body, tooltip);
    });
  }

  hide() {
    window.setTimeout(() => {
      if (this.tooltip) {
        this.renderer.removeClass(this.tooltip, 'ng-tooltip-show');
        this.renderer.removeChild(document.body, this.tooltip);
        this.tooltip = undefined;
      }
    }, this.delay);
  }

  create() {
    this.tooltip = this.renderer.createElement('span');

    this.renderer.appendChild(
      this.tooltip,
      this.renderer.createText(this.tooltipTitle) // textNode
    );

    this.renderer.appendChild(document.body, this.tooltip);
    this.renderer.addClass(this.tooltip, 'ng-tooltip');
    this.renderer.addClass(this.tooltip, `ng-tooltip-${this.placement}`);

    this.renderer.setStyle(this.tooltip, '-webkit-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, '-moz-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, '-o-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, 'transition', `opacity ${this.delay}ms`);
    // Position off-screen initially to get accurate dimensions
    this.renderer.setStyle(this.tooltip, 'position', 'fixed');
    this.renderer.setStyle(this.tooltip, 'top', '-9999px');
    this.renderer.setStyle(this.tooltip, 'left', '-9999px');
  }

  setPosition() {
    const hostPos = this.el.nativeElement?.getBoundingClientRect();
    if (!hostPos || !this.tooltip) return;

    // Get tooltip dimensions - ensure it's rendered first
    const tooltipWidth = this.tooltip.offsetWidth || this.tooltip.getBoundingClientRect().width;
    const tooltipHeight = this.tooltip.offsetHeight || this.tooltip.getBoundingClientRect().height;

    let top, left;

    if (this.placement === 'top') {
      top = hostPos.top - tooltipHeight - this.offset;
      left = hostPos.left + (hostPos.width / 2) - (tooltipWidth / 2);
    } else if (this.placement === 'bottom') {
      top = hostPos.bottom + this.offset;
      left = hostPos.left + (hostPos.width / 2) - (tooltipWidth / 2);
    } else if (this.placement === 'left') {
      top = hostPos.top + (hostPos.height / 2) - (tooltipHeight / 2);
      left = hostPos.left - tooltipWidth - this.offset;
    } else if (this.placement === 'right') {
      top = hostPos.top + (hostPos.height / 2) - (tooltipHeight / 2);
      left = hostPos.right + this.offset;
    }

    // Use fixed positioning so it stays relative to viewport on scroll
    this.renderer.setStyle(this.tooltip, 'position', 'fixed');
    this.renderer.setStyle(this.tooltip, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }

  public getTooltipText(): string {
    const classList = this.el.nativeElement.classList;

    if (classList.contains('status-active')) {
      return 'Active';
    } else if (classList.contains('status-inactive')) {
      return 'Inactive';
    }

    return '';
  }
}

//tooltip="I am tool" placement="left" [delay]="100"
